import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  BaseSelectField,
  BaseTextField,
  DefaultButton,
  PageContentPreloader,
  PageLayout,
  PaginationTable,
} from 'common/components';
import { exportUserTagLogs, getUserTagLogs, getUserTagLogFilters } from '../../_redux';

import { staticStyles } from './style';

const EMPTY_FILTERS = {
  operator: '',
  customer: '',
  action: '',
  tag: '',
  start_date: '',
  end_date: '',
};

const queryToFilters = query =>
  Object.keys(EMPTY_FILTERS).reduce((filters, key) => ({ ...filters, [key]: query[key] || '' }), EMPTY_FILTERS);

const UserCell = ({ user }) => (
  <div className="UserTagLogsTable__user">
    <span className="UserTagLogsTable__user-name">{user?.name || '-'}</span>
    <span className="UserTagLogsTable__user-email">{user?.email || '-'}</span>
  </div>
);

UserCell.propTypes = {
  user: PropTypes.object,
};

UserCell.defaultProps = {
  user: null,
};

const ChangeCell = ({ tags }) => (
  <div className="UserTagLogsTable__change">
    {tags?.length ? (
      tags.map(tag => (
        <span className="UserTagLogsTable__tag" key={tag.id}>
          {tag.name}
        </span>
      ))
    ) : (
      <span className="UserTagLogsTable__none">None</span>
    )}
  </div>
);

ChangeCell.propTypes = {
  tags: PropTypes.array,
};

ChangeCell.defaultProps = {
  tags: [],
};

/* eslint-disable react/prop-types */
const USER_TAG_LOG_COLUMNS = [
  {
    Header: () => <span>ID</span>,
    renderHeader: true,
    accessor: 'id',
    Cell: ({ row: { original } }) => <span>LOG{original.id}</span>,
  },
  {
    Header: () => <span>Operator</span>,
    renderHeader: true,
    accessor: 'operator',
    Cell: ({ row: { original } }) => <UserCell user={original.operator} />,
  },
  {
    Header: () => <span>Customer</span>,
    renderHeader: true,
    accessor: 'customer',
    Cell: ({ row: { original } }) => <UserCell user={original.customer} />,
  },
  {
    Header: () => <span>Tag</span>,
    renderHeader: true,
    accessor: 'tags',
    Cell: ({ row: { original } }) => <ChangeCell tags={original.tags || (original.tag ? [original.tag] : [])} />,
  },
  {
    Header: () => <span>Type</span>,
    renderHeader: true,
    accessor: 'type',
    Cell: ({ row: { original } }) => (
      <span className={`UserTagLogsTable__type UserTagLogsTable__type--${original.type}`}>{original.type}</span>
    ),
  },
  {
    Header: () => <span>Change (Before → After)</span>,
    renderHeader: true,
    accessor: 'afterTags',
    Cell: ({ row: { original } }) => (
      <div className="UserTagLogsTable__change">
        <ChangeCell tags={original.beforeTags} />
        <span className="UserTagLogsTable__arrow">→</span>
        <ChangeCell tags={original.afterTags} />
      </div>
    ),
  },
  {
    Header: () => <span>Time</span>,
    renderHeader: true,
    accessor: 'created',
    Cell: ({ row: { original } }) => <span>{new Date(original.created).toLocaleString()}</span>,
  },
];
/* eslint-enable react/prop-types */

const UserTagLogsPage = ({ logs, logsAreLoaded, filters, exportIsLoading, getLogs, getFilters, exportLogs }) => {
  const history = useHistory();
  const location = useLocation();
  const query = useMemo(() => ({ page: 1, page_size: 10, ...queryString.parse(location.search) }), [location.search]);
  const [filterValues, setFilterValues] = useState(() => queryToFilters(query));

  useEffect(() => {
    getFilters();
  }, [getFilters]);

  useEffect(() => {
    setFilterValues(queryToFilters(query));
    getLogs({ search: query });
  }, [getLogs, location.search, query]);

  const optionGroups = useMemo(
    () => ({
      operators: [{ value: '', label: 'All operators' }, ...(filters.operators || [])],
      customers: [{ value: '', label: 'All customers' }, ...(filters.customers || [])],
      actions: [{ value: '', label: 'All actions' }, ...(filters.actions || [])],
      tags: [{ value: '', label: 'All tags' }, ...(filters.tags || [])],
    }),
    [filters]
  );

  const selectedOption = (name, options) =>
    options.find(option => option.value.toString() === filterValues[name].toString()) || options[0];

  const handleSelectChange = name => option => {
    setFilterValues(values => ({ ...values, [name]: option?.value || '' }));
  };

  const handleDateChange = name => event => {
    setFilterValues(values => ({ ...values, [name]: event.target.value }));
  };

  const buildSearch = () => {
    const search = { page: 1, page_size: query.page_size || 10 };
    Object.keys(filterValues).forEach(key => {
      if (filterValues[key]) search[key] = filterValues[key];
    });
    return search;
  };

  const handleSearch = event => {
    event.preventDefault();
    const search = buildSearch();
    history.replace({ ...location, search: queryString.stringify(search) });
  };

  const handleReset = () => {
    const search = { page: 1, page_size: query.page_size || 10 };
    setFilterValues(EMPTY_FILTERS);
    history.replace({ ...location, search: queryString.stringify(search) });
  };

  const handleExport = () => {
    const exportSearch = Object.keys(query).reduce((search, key) => {
      if (!['page', 'page_size'].includes(key) && query[key]) search[key] = query[key];
      return search;
    }, {});
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    exportLogs({ ...exportSearch, timezone: browserTimezone });
  };

  return (
    <PageLayout caption="User Tag Logs">
      <PageContentPreloader ready={logsAreLoaded} type="bigLine" firstLaunchOnly>
        <form className="UserTagLogsPage__filters" onSubmit={handleSearch}>
          <BaseSelectField
            name="operator"
            label="Operator"
            value={selectedOption('operator', optionGroups.operators)}
            options={optionGroups.operators}
            searchable
            onChange={handleSelectChange('operator')}
          />
          <BaseSelectField
            name="customer"
            label="Customer"
            value={selectedOption('customer', optionGroups.customers)}
            options={optionGroups.customers}
            searchable
            onChange={handleSelectChange('customer')}
          />
          <BaseSelectField
            name="action"
            label="Action"
            value={selectedOption('action', optionGroups.actions)}
            options={optionGroups.actions}
            onChange={handleSelectChange('action')}
          />
          <BaseSelectField
            name="tag"
            label="Tag"
            value={selectedOption('tag', optionGroups.tags)}
            options={optionGroups.tags}
            searchable
            onChange={handleSelectChange('tag')}
          />
          <BaseTextField
            name="start_date"
            type="date"
            text="Start date"
            value={filterValues.start_date}
            onChange={handleDateChange('start_date')}
          />
          <BaseTextField
            name="end_date"
            type="date"
            text="End date"
            value={filterValues.end_date}
            onChange={handleDateChange('end_date')}
          />
          <DefaultButton textId="justSearch" type="submit" filled />
          <DefaultButton textId="justReset" type="button" onClick={handleReset} />
        </form>

        <div className="UserTagLogsTable">
          <div className="UserTagLogsTable__actions">
            <DefaultButton
              textId="justExportCsv"
              type="button"
              onClick={handleExport}
              loading={exportIsLoading}
              disabled={exportIsLoading}
            />
          </div>
          <PaginationTable
            data={logs}
            columns={USER_TAG_LOG_COLUMNS}
            loading={!logsAreLoaded}
            getList={getLogs}
            location={location}
            history={history}
            params={{ search: query }}
            isNewTable
            noResponsive
            withoutSort
          />
        </div>
      </PageContentPreloader>
      <style jsx global>
        {staticStyles}
      </style>
    </PageLayout>
  );
};

UserTagLogsPage.propTypes = {
  logs: PropTypes.object.isRequired,
  logsAreLoaded: PropTypes.bool.isRequired,
  filters: PropTypes.object.isRequired,
  exportIsLoading: PropTypes.bool.isRequired,
  getLogs: PropTypes.func.isRequired,
  getFilters: PropTypes.func.isRequired,
  exportLogs: PropTypes.func.isRequired,
};

export default compose(
  connect(
    state => ({
      logs: state.backoffice.userTagLogs.logs,
      logsAreLoaded: state.backoffice.userTagLogs.logsAreLoaded,
      filters: state.backoffice.userTagLogs.filters,
      exportIsLoading: state.backoffice.userTagLogs.exportIsLoading,
    }),
    {
      getLogs: search => getUserTagLogs.request(search),
      getFilters: () => getUserTagLogFilters.request(),
      exportLogs: search => exportUserTagLogs.request(search),
    }
  )
)(UserTagLogsPage);

export { UserTagLogsPage };
